import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {GetSizeSelectionResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/size_selection/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {ProductTrackingData} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import {
  AddToBasketTrackingData,
  BasketMutationError,
  useIsSizeInBasket,
} from '@aboutyou/user';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CaptchaFlyout} from '../Captcha/CaptchaFlyout';
import {useActivatedCouponState} from '../Coupons/useActivatedCouponState';
import {useCanShowCoupons} from '../Coupons/useCanShowCoupons';
import {useShouldShowMovReachedLabel} from '../FreeDeliverySectionView/utils/useShouldShowMovReachedLabel';
import {AddToBasketPayload} from '../ProductTile/types';
import {
  AddToBasketOnSuccessProps,
  useAddToBasket,
} from './hooks/useAddToBasket';
import {useAddToBasketErrorToast} from './hooks/useAddToBasketErrorToast';
import {
  SharedFlyoutBase,
  SharedFlyoutView as SharedFlyoutBaseWithoutRequest,
} from './sharedFlyoutBase';

interface AddToBasketFlyoutProps {
  isOpen: boolean;
  /** @param onClose when nested flyouts are used, different closing fn must be called on the wishlist */
  onClose: (options?: {isNested: boolean}) => void;
  product: AddToBasketPayload['product'];
  preselectedSize?: SizeSelection_Size;
  withSuccessToast?: boolean;
  withErrorToast?: boolean;
  /** @param sizeSelectionData Existing size selection data.
   * This will ignore the size selection request and create the UI from already
   * available data. Used at ADP, where the sizeselection data comes as pard of
   * product bulk request.
   */
  sizeSelectionData?: GetSizeSelectionResponse;
  onSuccess?: (props: AddToBasketOnSuccessProps) => void;
  onError?: (error?: BasketMutationError) => void;
  trackingData?: ProductTrackingData & {brandName: string};
  onCaptchaClose?: () => void;
  onSizeAdvisorSizeSelection?: (sizeId: number) => void;
  openedStateManagerKey?: string;
  disableCouponsSlider?: boolean;
  renderInPortal?: boolean;
  className?: string;
  overrideBETrackingData?: AddToBasketTrackingData;
  couponPromotionId?: string;
  badgesListKeys: string[];
}

export const AddToBasketFlyout = ({
  openedStateManagerKey,
  isOpen,
  product,
  preselectedSize,
  withSuccessToast,
  withErrorToast,
  onSuccess,
  onError,
  onClose,
  onCaptchaClose,
  sizeSelectionData,
  trackingData,
  onSizeAdvisorSizeSelection,
  disableCouponsSlider,
  renderInPortal,
  className,
  overrideBETrackingData,
  couponPromotionId: preselectedCouponPromotionId,
  badgesListKeys,
}: AddToBasketFlyoutProps) => {
  const [selectedSize, setSelectedSize] = useState<
    SizeSelection_Size | undefined
  >(preselectedSize);
  const showToast = useShowToast();
  const showAddToBasketErrorToast = useAddToBasketErrorToast();
  const canShowCoupons = useCanShowCoupons();
  const isInBasket = useIsSizeInBasket();

  const {couponPromotionId} = useActivatedCouponState(product.id);

  const {shouldShowMovReachedLabel} = useShouldShowMovReachedLabel();

  const {t} = useTranslation();

  const {
    cancel: cancelCaptchaFlyout,
    isOpen: isCaptchaFlyoutOpen,
    open: openCaptchaFlyout,
  } = useOpenedStateManager(`${product.id}-captcha${openedStateManagerKey}`);

  const captchaSucceededRef = useRef(false);
  const isCaptchaFlyoutOpenRef = useRef(isCaptchaFlyoutOpen);

  // The captcha opened state lives in the history, so it can also close via
  // a browser-back pop, which never invokes the CaptchaFlyout onClose prop.
  // Reacting to the state transition here is the only way to catch every
  // close and let consumers release their own opened states (onCaptchaClose).
  useEffect(() => {
    const wasOpen = isCaptchaFlyoutOpenRef.current;
    isCaptchaFlyoutOpenRef.current = isCaptchaFlyoutOpen;

    if (!wasOpen || isCaptchaFlyoutOpen) {
      return;
    }

    if (captchaSucceededRef.current) {
      captchaSucceededRef.current = false;
      return;
    }

    onCaptchaClose?.();
  }, [isCaptchaFlyoutOpen, onCaptchaClose]);

  const handleAddToBasketSuccessWithMovData = (
    props: AddToBasketOnSuccessProps,
  ) => {
    if (withSuccessToast) {
      showToast({
        content: t('Added to basket'),
        type: 'SUCCESS',
        'data-testid': `${product.id}_successToast`,
      });
    }

    onSuccess?.(props);
  };

  const handleAddToBasketError = useCallback(
    (error: BasketMutationError) => {
      // The live ref decides between reopening the captcha and toasting.
      // State captured in this closure goes stale between solving a captcha
      // and the server rejecting the solved code, which would leave the
      // captcha closed with no way to retry the add.
      if (error.captchaFailed && !isCaptchaFlyoutOpenRef.current) {
        openCaptchaFlyout();
      } else if (withErrorToast) {
        showAddToBasketErrorToast(error);
      }
      onError?.(error);
    },
    [openCaptchaFlyout, onError, withErrorToast, showAddToBasketErrorToast],
  );

  const addToBasket = useAddToBasket({
    onSuccess: handleAddToBasketSuccessWithMovData,
    onError: handleAddToBasketError,
    trackingData,
    badgesListKeys,
  });

  const handleAddToBasket = async (oneSize?: SizeSelection_Size) => {
    const sizeToAdd = oneSize || selectedSize || preselectedSize;

    if (!sizeToAdd) {
      return;
    }

    // Sizes already in the basket passed the captcha on their first add;
    // quantity increments don't need to prove humanity again (mirrors the
    // inline captcha skip in AddToBasket.tsx).
    if (product.captchaRequired && !isInBasket(sizeToAdd.sizeId)) {
      // Persist the size for handleCaptchaSuccess: one-size and preselected
      // sizes reach this point without the user ever tapping a size, so
      // selectedSize would otherwise still be undefined after the captcha.
      setSelectedSize(sizeToAdd);
      return openCaptchaFlyout();
    }

    if (oneSize) {
      setSelectedSize(oneSize);
    } else {
      onClose();
    }

    await addToBasket({
      size: sizeToAdd,
      productId: product.id,
      sponsoredType: product.sponsoredType,
      couponPromotionId,
      skipFreeDeliveryMovSection: !shouldShowMovReachedLabel,
      overrideBETrackingData,
    });
  };

  const selectSize = useCallback((size: SizeSelection_Size) => {
    setSelectedSize(size);
  }, []);

  const onCancel = () => {
    setSelectedSize(preselectedSize);
    onClose();
  };

  const handleCaptchaSuccess = async (captchaCode: string) => {
    captchaSucceededRef.current = true;
    cancelCaptchaFlyout();

    if (selectedSize) {
      setTimeout(() => {
        onClose();
      }, 100);
      await addToBasket({
        size: selectedSize,
        productId: product.id,
        sponsoredType: product.sponsoredType,
        couponPromotionId,
        captchaCode,
        skipFreeDeliveryMovSection: !shouldShowMovReachedLabel,
        overrideBETrackingData,
      });
    }
  };

  const selectedSizeId = selectedSize
    ? selectedSize.sizeId
    : preselectedSize?.sizeId;

  const sharedProps = {
    isOpen,
    onSelect: selectSize,
    selectedSizeId,
    setSelectedSize,
    onSizeAdvisorSizeSelection,
    onAddToBasket: handleAddToBasket,
    onCancel,
    disableCouponsSlider: !canShowCoupons || disableCouponsSlider,
    renderInPortal,
    className,
  };

  return (
    <>
      <CaptchaFlyout
        renderInPortal
        isOpen={isCaptchaFlyoutOpen}
        onClose={cancelCaptchaFlyout}
        onSuccess={handleCaptchaSuccess}
        captchaUniqueKey="addToBasketFlyout"
      />
      {sizeSelectionData ? (
        <SharedFlyoutBaseWithoutRequest
          {...sharedProps}
          {...sizeSelectionData}
          couponPromotionId={preselectedCouponPromotionId}
        />
      ) : (
        <SharedFlyoutBase
          {...sharedProps}
          isCaptchaRequired
          productId={product.id}
          couponPromotionId={preselectedCouponPromotionId}
        />
      )}
    </>
  );
};
